import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';
import { useEffect, useState } from 'react';
import playSvg from '../../../img/play.svg';
import { useTranslation } from '../../../lib/i18n';
import {
  POST_PREVIEW_IMAGE_WIDTH,
  calc16to9ImageProps,
  img16to9Loader,
} from '../../../lib/images';
import { VideoContext } from './video-context';
import { createPortal } from 'react-dom';

const DynamicYouTubePlayer = dynamic(() => import('./youtube-player'));

export const getVideoId = (videoUrl) =>
  videoUrl.replace('https://www.youtube.com/watch?v=', '');

export const VideoPreview = ({
  video,
  image,
  className = '',
  onClick = null,
  width = POST_PREVIEW_IMAGE_WIDTH,
  preload = false,
}) => {
  const { t } = useTranslation();
  image = image
    ? image
    : { url: video?.thumbnail_url, dimensions: { width: 900, height: 506 } };

  calc16to9ImageProps(image, width);

  return (
    <div className='relative'>
      <Image loader={img16to9Loader} priority={preload} {...image[width]} />
      <div
        className={
          className +
          ' absolute bottom-0 left-0 right-0 top-0 flex flex-col justify-center bg-black opacity-20'
        }
      />

      <button
        className='absolute bottom-0 top-0 z-10 block w-full'
        aria-label={t('play')}
        onClick={onClick}
      >
        <img src={playSvg} alt={t('play')} className='mx-auto' />
      </button>
    </div>
  );
};

export const Video2 = ({
  videoUrl = null,
  video = null,
  image = null,
  className = '',
  trackingLabel = '',
  width = POST_PREVIEW_IMAGE_WIDTH,
  preload = false,
}) => {
  const [videoVisible, setVideoVisible] = useState(false);

  const onClick = (e) => {
    e.preventDefault();
    setVideoVisible(true);
  };

  if (videoUrl && !video) {
    const vid = getVideoId(videoUrl);
    video = {
      embed_url: videoUrl,
      id: vid,
      thumbnail_url: `https://img.youtube.com/vi/${vid}/maxresdefault.jpg`,
    };
  }

  return (
    <div className={className}>
      {videoVisible ? (
        <VideoContext.Provider
          value={{
            videoId: video.id ? video.id : getVideoId(video.embed_url),
            trackingLabel,
            onEnd: () => setVideoVisible(false),
          }}
        >
          <DynamicYouTubePlayer />
        </VideoContext.Provider>
      ) : (
        <VideoPreview
          image={image}
          video={video}
          onClick={onClick}
          className={className}
          width={width}
          preload={preload}
        />
      )}
    </div>
  );
};



export const Video = ({ 
  videoUrl = null,
  video = null,
  image = null,
  className = '',
  trackingLabel = '',
  width = POST_PREVIEW_IMAGE_WIDTH,
  preload = false,

}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  calc16to9ImageProps(image, width);

  if (videoUrl && !video) {
    const vid = getVideoId(videoUrl);
    video = {
      embed_url: videoUrl,
      id: vid,
      thumbnail_url: `https://img.youtube.com/vi/${vid}/maxresdefault.jpg`,
    };
  }

  useEffect(() => {
    if (isOverlayOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOverlayOpen]);

  const handleClose = () => {
    setIsOverlayOpen(false);
  };

  const handleOverlayClick = (e) => {
    // Close overlay if clicking directly on the overlay background
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <>
      <div className={`${className}`}>
          <div className='relative aspect-video w-full overflow-hidden border border-gray-200 shadow-lg'>
          {image ? (
          <Image loader={img16to9Loader} priority={preload} {...image[width]} />
          ) : (
              <img src={video?.thumbnail_url} className='mx-auto' alt='Play video'/>
          )}
          {!videoUrl && video && (
            <div className='absolute inset-0 flex items-center justify-center cursor-pointer'>
              <button
                className='absolute bottom-0 top-0 z-10 block w-full'
                onClick={() => {
                  setIsOverlayOpen(true);
                }}
                type='button'
              >
                <img src={playSvg} className='mx-auto' alt='Play video' />
              </button>
            </div>
          )}
          </div>
      </div>

      {/* Full-screen overlay */}
      {isOverlayOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <>
            <style>{`
              .video-overlay {
                display: grid;
                place-items: center;
              }
              .video-overlay__container {
                position: relative;
                width: min(90vw, 1200px);
                max-height: 90vh;
                aspect-ratio: 16 / 9;
              }
              .video-overlay__container .youtubeWrapper {
                position: relative;
                width: 100%;
                height: 100%;
              }
              .video-overlay__container .youtubeWrapper iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
              }
            `}</style>
            <div 
              className='video-overlay fixed inset-0 z-[9999] bg-black bg-opacity-80 p-4'
              onClick={handleOverlayClick}
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                className='absolute top-4 right-4 z-[10000] text-white hover:text-gray-300 transition-colors'
                aria-label='Close video'
                type='button'
              >
                <svg
                  className='w-8 h-8'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>

              {/* Video container */}
              <div 
                className='video-overlay__container'
                onClick={(e) => e.stopPropagation()}
              >
                {/* @ts-ignore */}
                <VideoContext.Provider
                  value={{
                    videoId: video.id ? video.id : getVideoId(video.embed_url),
                    trackingLabel,
                    onEnd: handleClose,
                  }}
                >
                  <DynamicYouTubePlayer />
                </VideoContext.Provider>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

